import React, { useEffect, useState, useRef } from 'react';
import { Link, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { Loader, Upload, X, MapPin } from 'lucide-react';
import PublicNav from '../components/PublicNav';
import Footer from '../components/Footer';
import { API_BASE_URL } from '../config';
import { convertHeicToJpeg, isHeicFile } from '../utils/convertHeic';

function usePublicUserId() {
  try {
    const raw = localStorage.getItem('userData') || localStorage.getItem('user');
    if (!raw) return null;
    return JSON.parse(raw)?.id || null;
  } catch {
    return null;
  }
}

function readLoggedInEmail() {
  try {
    const raw = localStorage.getItem('userData') || localStorage.getItem('user');
    if (!raw) return '';
    const u = JSON.parse(raw);
    return (u.email || u.userEmail || '').trim();
  } catch {
    return '';
  }
}

function readAccountDisplayName() {
  try {
    const raw = localStorage.getItem('userData') || localStorage.getItem('user');
    if (!raw) return '';
    const u = JSON.parse(raw);
    return (u.display_name || u.fullName || u.full_name || u.name || '').trim();
  } catch {
    return '';
  }
}

function compressImage(file, maxDim = 1920, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new window.Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let w = img.width;
        let h = img.height;
        if (w > maxDim || h > maxDim) {
          if (w > h) {
            h = Math.round((h * maxDim) / w);
            w = maxDim;
          } else {
            w = Math.round((w * maxDim) / h);
            h = maxDim;
          }
        }
        canvas.width = w;
        canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const YARD_BRIEF_PREFILL_STORAGE = 'yardBriefPrefill';

export default function HomeownerBriefPage() {
  const { designerId } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const currentUserId = usePublicUserId();
  const fileInputRef = useRef(null);
  const prefillAppliedRef = useRef(false);

  const [designer, setDesigner] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loadingDesigner, setLoadingDesigner] = useState(true);

  const [homeownerFullName, setHomeownerFullName] = useState('');
  const [homeownerEmail, setHomeownerEmail] = useState('');
  const [city, setCity] = useState('');
  const [mainGoal, setMainGoal] = useState('');
  const [answer1, setAnswer1] = useState('');
  const [answer2, setAnswer2] = useState('');
  const [answer3, setAnswer3] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [done, setDone] = useState(null);

  const api = API_BASE_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');

  useEffect(() => {
    document.title = 'Send a yard brief | PlantaDay';
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingDesigner(true);
      setLoadError(null);
      try {
        const res = await fetch(`${api}/designers/${encodeURIComponent(designerId)}/public`);
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Designer not found');
        if (!cancelled) setDesigner(data.designer);
      } catch (e) {
        if (!cancelled) setLoadError(e.message || 'Could not load designer');
      } finally {
        if (!cancelled) setLoadingDesigner(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [designerId, api]);

  useEffect(() => {
    if (prefillAppliedRef.current) return;
    const qidUrl = searchParams.get('questionId');
    let pre = location.state?.yardBriefPrefill;
    if (!pre) {
      try {
        const raw = sessionStorage.getItem(YARD_BRIEF_PREFILL_STORAGE);
        if (raw) pre = JSON.parse(raw);
      } catch {
        /* ignore */
      }
    }
    const qid = qidUrl || pre?.questionId;
    if (!pre && !qid) return;
    prefillAppliedRef.current = true;
    try {
      sessionStorage.removeItem(YARD_BRIEF_PREFILL_STORAGE);
    } catch {
      /* ignore */
    }

    if (pre?.questionText) {
      let mg = pre.questionText.trim();
      if (qid) mg = `${mg}\n\n(PlantADay question ID: ${qid})`;
      setMainGoal(mg);
    } else if (qid) {
      setMainGoal(`(PlantADay question ID: ${qid})`);
    }

    if (pre?.email) setHomeownerEmail(pre.email);
    else if (readLoggedInEmail()) setHomeownerEmail(readLoggedInEmail());

    if (pre?.city) setCity(pre.city);

    const acctName = readAccountDisplayName();
    if (acctName) setHomeownerFullName(acctName);

    if (pre?.photoDataUrl && typeof pre.photoDataUrl === 'string') {
      (async () => {
        try {
          const res = await fetch(pre.photoDataUrl);
          const blob = await res.blob();
          const file = new File([blob], 'yard-photo.jpg', { type: blob.type || 'image/jpeg' });
          setPhotoFile(file);
          setPhotoPreview(pre.photoDataUrl);
        } catch {
          /* user can upload manually */
        }
      })();
    }
  }, [location.state, searchParams]);

  const handlePhoto = async (e) => {
    let file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      setSubmitError('Photo must be under 15 MB.');
      return;
    }
    setSubmitError(null);
    if (isHeicFile(file)) {
      try {
        file = await convertHeicToJpeg(file);
      } catch (err) {
        setSubmitError(err.message || 'Could not convert HEIC image. Please try a JPG or PNG.');
        return;
      }
    }
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setPhotoPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const clearPhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    if (!photoFile) {
      setSubmitError('Please add a yard photo.');
      return;
    }
    setSubmitting(true);
    try {
      let fileToSend = photoFile;
      if (photoFile.size > 2 * 1024 * 1024) {
        const dataUrl = await compressImage(photoFile);
        const r = await fetch(dataUrl);
        const b = await r.blob();
        fileToSend = new File([b], 'yard-photo.jpg', { type: 'image/jpeg' });
      }
      const fd = new FormData();
      fd.append('photo', fileToSend);
      fd.append('designerId', designerId);
      fd.append('homeownerFullName', homeownerFullName.trim());
      fd.append('homeownerEmail', homeownerEmail.trim());
      fd.append('city', city.trim());
      fd.append('mainGoal', mainGoal.trim());
      fd.append('answer1', answer1.trim());
      fd.append('answer2', answer2.trim());
      fd.append('answer3', answer3.trim());

      const res = await fetch(`${api}/public/homeowner-brief`, {
        method: 'POST',
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Something went wrong. Please try again.');
      }
      setDone({
        designerName: data.designerName || designer?.name || designer?.businessName || 'your designer',
        homeownerEmail: homeownerEmail.trim(),
      });
    } catch (err) {
      setSubmitError(err.message || 'Failed to send brief.');
    } finally {
      setSubmitting(false);
    }
  };

  const designerLabel = designer?.businessName || designer?.name || 'this designer';

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <PublicNav currentUserId={currentUserId} />
      <main className="flex-1 max-w-2xl mx-auto px-4 py-10 sm:py-14 w-full">
        <Link to="/find-designer" className="text-sm font-medium text-green-700 hover:underline">
          ← Find a designer
        </Link>

        {loadingDesigner && (
          <div className="mt-10 flex justify-center text-gray-500">
            <Loader className="w-8 h-8 animate-spin" />
          </div>
        )}

        {loadError && (
          <div className="mt-8 p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-900 text-sm">
            {loadError}
          </div>
        )}

        {designer && !done && (
          <>
            <h1 className="mt-6 text-3xl font-bold text-gray-900">Send a yard brief</h1>
            <p className="mt-2 text-gray-600">
              You are sending a brief to <span className="font-semibold text-gray-900">{designerLabel}</span>.
              Add a photo and answer three short questions. They will see this in their PlantADay project when they log in.
            </p>

            <form onSubmit={handleSubmit} className="mt-8 space-y-6 bg-white rounded-2xl border border-gray-200 shadow-sm p-6 sm:p-8">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Your full name</label>
                <input
                  required
                  type="text"
                  value={homeownerFullName}
                  onChange={(e) => setHomeownerFullName(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-gray-900"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  required
                  type="email"
                  autoComplete="email"
                  value={homeownerEmail}
                  onChange={(e) => setHomeownerEmail(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-gray-900"
                />
                <p className="mt-1 text-xs text-gray-500">We use this for your client invitation and updates from the designer.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  Your city or area
                </label>
                <input
                  required
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="e.g. Victoria BC"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-gray-900"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Main goal for your yard</label>
                <textarea
                  required
                  rows={2}
                  value={mainGoal}
                  onChange={(e) => setMainGoal(e.target.value)}
                  placeholder="e.g. More privacy, less lawn, better curb appeal"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-gray-900 resize-none"
                />
              </div>

              <div>
                <p className="block text-sm font-medium text-gray-700 mb-2">Yard photo</p>
                {photoPreview ? (
                  <div className="relative inline-block">
                    <img src={photoPreview} alt="Your yard" className="h-48 rounded-xl object-cover border border-gray-200" />
                    <button
                      type="button"
                      onClick={clearPhoto}
                      className="absolute -top-2 -right-2 w-7 h-7 bg-white border border-gray-300 rounded-full flex items-center justify-center shadow-sm"
                      aria-label="Remove photo"
                    >
                      <X className="w-4 h-4 text-gray-600" />
                    </button>
                  </div>
                ) : (
                  <label className="flex items-center gap-3 px-4 py-3 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-green-400 w-full sm:w-auto inline-flex">
                    <Upload className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    <span className="text-sm text-gray-600">Upload a yard photo</span>
                    <input ref={fileInputRef} type="file" accept="image/*,image/heic,image/heif,.heic,.heif" className="sr-only" onChange={handlePhoto} />
                  </label>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">What do you want this space to do for you?</label>
                <textarea
                  required
                  rows={3}
                  value={answer1}
                  onChange={(e) => setAnswer1(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-gray-900 resize-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">What is not working about it right now?</label>
                <textarea
                  required
                  rows={3}
                  value={answer2}
                  onChange={(e) => setAnswer2(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-gray-900 resize-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Anything you love or hate in other yards?</label>
                <textarea
                  required
                  rows={3}
                  value={answer3}
                  onChange={(e) => setAnswer3(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-gray-900 resize-none"
                />
              </div>

              {submitError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">{submitError}</div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full sm:w-auto px-6 py-3 rounded-xl bg-green-600 text-white font-semibold hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader className="w-5 h-5 animate-spin" />
                    Sending…
                  </span>
                ) : (
                  'Send to a designer'
                )}
              </button>
            </form>
          </>
        )}

        {done && (
          <div className="mt-10 rounded-2xl border border-green-200 bg-green-50/90 p-8 text-center">
            <h1 className="text-2xl font-bold text-green-900">Brief sent</h1>
            <p className="mt-4 text-green-900/90 leading-relaxed">
              Your brief has been sent to {done.designerName}.
            </p>
            <p className="mt-3 text-green-900/90 leading-relaxed">
              We also emailed you a link to check back on this project. Check your inbox at{' '}
              <span className="font-medium text-green-950">{done.homeownerEmail}</span>.
            </p>
            <p className="mt-3 text-sm text-green-800/85 leading-relaxed">
              Check your spam folder if you do not see it within a few minutes.
            </p>
            <p className="mt-4 text-green-900/90 leading-relaxed text-sm">
              You will get an email when they respond.
            </p>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <Link to="/find-designer" className="text-sm font-semibold text-green-800 hover:underline">
                Back to Find a designer
              </Link>
              <Link to="/" className="text-sm font-semibold text-gray-600 hover:underline">
                Home
              </Link>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
